import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Send, X, Plus, MessageSquare, Trash2, Pencil, Pin, PinOff, Search,
  Bot, User as UserIcon, ArrowUpRight, AlertTriangle, MessageCircle, History, Zap,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m_${Date.now()}_${Math.random()}`;

// Page-specific greeting + quick actions (context-aware).
const PAGE_CONFIG = {
  "/dashboard": { label: "Dashboard", greeting: "I can recommend your next best step and explain anything on your dashboard.",
    actions: [["Recommend My Next Step", "Based on my progress, what is the single most important next step I should take, and why?"], ["Review My Progress", "Review my overall business progress and tell me what's going well and what needs attention."], ["Summarize My Business", "Summarize my business based on my profile and progress so far."]] },
  "/formation": { label: "Business Formation", greeting: "I can explain entity types and help you prepare your formation checklist.",
    actions: [["Explain Entity Types", "Explain the differences between LLC, S-Corp, C-Corp and sole proprietorship for my situation."], ["Formation Checklist", "Create a step-by-step formation checklist for my business."], ["Explain This Page", "Explain what I should do on the Business Formation page."]] },
  "/credit": { label: "Business Credit", greeting: "I can help you build business credit the right way.",
    actions: [["Help Me Build Credit", "What are the first steps to build business credit for my company?"], ["Explain This Page", "Explain what the Business Credit module helps me do."], ["Create an Action Plan", "Create a business credit action plan for the next 90 days."]] },
  "/funding": { label: "Funding", greeting: "I can help you assess funding readiness and prepare.",
    actions: [["Am I Funding Ready?", "Assess my funding readiness and tell me what to improve."], ["Create an Action Plan", "Create a funding-readiness action plan."], ["Explain This Page", "Explain what the Funding module helps me do."]] },
  "/documents": { label: "Documents", greeting: "I can help you create and improve business documents.",
    actions: [["Create a Document", "What business documents should I create next, and why?"], ["Explain This Page", "Explain what I can do in the Documents library."]] },
  "/contracts": { label: "Contracts", greeting: "I can help you understand and prepare contracts.",
    actions: [["Which Contracts Do I Need?", "Which contracts does my business need right now?"], ["Explain This Page", "Explain what the Contracts module helps me do."]] },
  "/compliance": { label: "Compliance", greeting: "I can help you stay on top of compliance deadlines.",
    actions: [["What Am I Missing?", "What compliance filings or obligations am I missing right now?"], ["Explain This Page", "Explain what the Compliance module tracks."]] },
  "/progress": { label: "Business Journey", greeting: "I can help you move through your business journey.",
    actions: [["Help Me Finish This Module", "Help me finish my current in-progress module."], ["Review My Progress", "Review my business journey and what to focus on next."]] },
  "/education": { label: "Academy", greeting: "I can suggest what to learn next.",
    actions: [["What Should I Learn Next?", "Based on my stage, what should I learn next?"], ["Explain This Page", "Explain what the Academy offers."]] },
  "/resources": { label: "Resources", greeting: "I can point you to the right resources.",
    actions: [["Find a Feature", "Help me find the right feature or resource for what I'm trying to do."]] },
  "/billing": { label: "Billing", greeting: "I can explain plans and what each unlocks.",
    actions: [["Explain My Plan", "Explain what my current plan includes and what an upgrade would unlock."]] },
};
const DEFAULT_CONFIG = { label: "Fairview", greeting: "How can I help you grow your business today?",
  actions: [["Recommend My Next Step", "What is the most important next step for my business right now?"], ["Explain This Page", "Explain what I can do on this page."], ["Find a Feature", "Help me find the right feature for what I'm trying to do."], ["Create a Checklist", "Create a prioritized checklist for my next milestone."]] };

function matchConfig(pathname) {
  const keys = Object.keys(PAGE_CONFIG).sort((a, b) => b.length - a.length);
  const hit = keys.find((k) => pathname.startsWith(k));
  return { path: hit || pathname, ...(hit ? PAGE_CONFIG[hit] : DEFAULT_CONFIG) };
}

function UsageBar({ usage, onUpgrade }) {
  if (!usage || usage.level === "unlimited") return null;
  const warn = ["warn80", "warn90", "reached"].includes(usage.level);
  const color = usage.level === "reached" || usage.level === "warn90" ? "text-red-300" : usage.level === "warn80" ? "text-amber-300" : "text-[#71717A]";
  return (
    <div className="px-4 py-2 border-b border-white/8 bg-[#0d0d0f]" data-testid="ai-coach-usage">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="flex items-center gap-1 text-[#A1A1AA]"><Zap className="w-3 h-3 text-[#D4AF37]" /> AI credits</span>
        <span className={color}>{usage.remaining} of {usage.limit} left</span>
      </div>
      <div className="h-1 rounded-full bg-white/8 overflow-hidden">
        <div className={`h-full rounded-full ${usage.level === "reached" || usage.level === "warn90" ? "bg-red-400" : usage.level === "warn80" ? "bg-amber-400" : "fv-gold-gradient"}`} style={{ width: `${usage.pct || 0}%` }} />
      </div>
      {warn && usage.upgrade_to && (
        <div className={`mt-2 flex items-center justify-between gap-2 text-[11px] ${color}`} data-testid="ai-coach-usage-warning">
          <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />
            {usage.level === "reached" ? "Monthly AI limit reached. Your work is saved." : usage.level === "warn90" ? "You're almost out of AI credits." : "You've used 80% of your AI credits."}</span>
          <button onClick={onUpgrade} data-testid="ai-coach-upgrade" className="shrink-0 text-[#D4AF37] font-medium hover:underline">Upgrade to {usage.upgrade_to}</button>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`coach-msg-${role}`}>
      <div className={`w-7 h-7 rounded-lg shrink-0 flex items-center justify-center ${isUser ? "bg-white/10" : "fv-gold-gradient"}`}>
        {isUser ? <UserIcon className="w-3.5 h-3.5 text-white" /> : <Bot className="w-3.5 h-3.5 text-[#0A0A0B]" />}
      </div>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-[#D4AF37]/15 text-white" : "bg-[#1C1C1F] text-[#E4E4E7]"}`}>{content}</div>
    </div>
  );
}

export default function AICoach() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | history
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const scrollRef = useRef(null);
  const cfg = matchConfig(location.pathname);

  const loadUsage = useCallback(() => api.get("/ai/usage").then(({ data }) => setUsage(data)).catch(() => {}), []);
  const loadConversations = useCallback((q = "") => api.get(`/ai/conversations${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setConversations(data)).catch(() => {}), []);

  useEffect(() => { if (open) { loadUsage(); loadConversations(); } }, [open, loadUsage, loadConversations]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const selectConversation = useCallback(async (id) => {
    setActiveId(id); setView("chat");
    try { const { data } = await api.get(`/ai/conversations/${id}/messages`); setMessages(data.map((m) => ({ ...m, _key: newKey() }))); }
    catch (e) { setMessages([]); }
  }, []);

  const newChat = useCallback(() => { setActiveId(null); setMessages([]); setView("chat"); }, []);

  const del = useCallback(async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/ai/conversations/${id}`); if (activeId === id) newChat(); loadConversations(search); toast.success("Conversation deleted"); }
    catch (er) { toast.error("Could not delete"); }
  }, [activeId, newChat, loadConversations, search]);

  const rename = useCallback(async (c, e) => {
    e?.stopPropagation();
    const title = window.prompt("Rename conversation", c.title);
    if (!title || !title.trim()) return;
    try { await api.patch(`/ai/conversations/${c.conversation_id}`, { title: title.trim() }); loadConversations(search); }
    catch (er) { toast.error("Could not rename"); }
  }, [loadConversations, search]);

  const togglePin = useCallback(async (c, e) => {
    e?.stopPropagation();
    try { await api.patch(`/ai/conversations/${c.conversation_id}`, { pinned: !c.pinned }); loadConversations(search); }
    catch (er) { toast.error("Could not update"); }
  }, [loadConversations, search]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { _key: newKey(), role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, conversation_id: activeId, page: cfg.path, page_label: cfg.label });
      setMessages((m) => [...m, { _key: newKey(), role: "assistant", content: data.reply }]);
      if (!activeId) setActiveId(data.conversation_id);
      loadConversations(); loadUsage();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = detail?.code;
      if (code === "usage_limit_reached") { toast.error("You've reached your monthly AI limit — your work is saved."); loadUsage(); }
      else if (err.response?.status === 429) toast.error(detail?.message || "Please slow down a moment.");
      else toast.error(formatApiError(detail) || "AI is unavailable");
      setMessages((m) => m.slice(0, -1));
    } finally { setLoading(false); }
  }, [input, loading, activeId, cfg, loadConversations, loadUsage]);

  const atLimit = usage && usage.level === "reached";

  return (
    <>
      {/* Floating button — on every authenticated page */}
      <button onClick={() => setOpen(true)} data-testid="ai-coach-fab" aria-label="Open AI Business Coach"
        className={`fixed z-40 bottom-5 right-5 w-14 h-14 rounded-full fv-gold-gradient shadow-lg shadow-black/40 flex items-center justify-center hover:scale-105 transition-transform ${open ? "hidden" : ""}`}>
        <Sparkles className="w-6 h-6 text-[#0A0A0B]" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 z-40 bg-black/40 sm:bg-transparent" />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              role="dialog" aria-label="AI Business Coach" data-testid="ai-coach-panel"
              className="fixed z-50 bg-[#0A0A0B] border-white/10 flex flex-col
                         inset-x-0 bottom-0 top-16 rounded-t-2xl border-t
                         sm:inset-y-0 sm:right-0 sm:left-auto sm:top-0 sm:w-[420px] sm:rounded-none sm:border-l">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl fv-gold-gradient flex items-center justify-center"><Sparkles className="w-4.5 h-4.5 text-[#0A0A0B]" /></div>
                  <div>
                    <div className="text-white font-medium text-sm leading-tight">AI Business Coach</div>
                    <div className="text-[10px] text-[#71717A]">GPT-5.5 · {cfg.label}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setView(view === "chat" ? "history" : "chat")} data-testid="ai-coach-toggle-view"
                    className="p-2 text-[#A1A1AA] hover:text-white rounded-lg" aria-label="Toggle history">
                    {view === "chat" ? <History className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                  </button>
                  <button onClick={newChat} data-testid="ai-coach-new" className="p-2 text-[#A1A1AA] hover:text-white rounded-lg" aria-label="New conversation"><Plus className="w-4 h-4" /></button>
                  <button onClick={() => setOpen(false)} data-testid="ai-coach-close" className="p-2 text-[#A1A1AA] hover:text-white rounded-lg" aria-label="Close coach"><X className="w-4 h-4" /></button>
                </div>
              </div>

              <UsageBar usage={usage} onUpgrade={() => { setOpen(false); navigate("/billing"); }} />

              {view === "history" ? (
                <div className="flex-1 flex flex-col min-h-0 p-3">
                  <div className="flex items-center gap-2 mb-3 bg-[#121214] border border-white/10 rounded-lg px-3 h-9">
                    <Search className="w-4 h-4 text-[#52525B]" />
                    <input value={search} onChange={(e) => { setSearch(e.target.value); loadConversations(e.target.value); }}
                      placeholder="Search conversations…" data-testid="ai-coach-search"
                      className="flex-1 bg-transparent text-sm text-white outline-none" />
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-1.5" data-testid="ai-coach-conversations">
                    {conversations.length === 0 && <p className="text-xs text-[#52525B] px-2 py-3">No conversations yet.</p>}
                    {conversations.map((c) => (
                      <div key={c.conversation_id} onClick={() => selectConversation(c.conversation_id)}
                        data-testid={`coach-conversation-${c.conversation_id}`}
                        className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${activeId === c.conversation_id ? "bg-[#D4AF37]/12 border border-[#D4AF37]/30" : "hover:bg-white/5 border border-transparent"}`}>
                        {c.pinned ? <Pin className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" /> : <MessageSquare className="w-3.5 h-3.5 text-[#71717A] shrink-0" />}
                        <span className="text-sm text-[#D4D4D8] truncate flex-1">{c.title}</span>
                        <button onClick={(e) => togglePin(c, e)} data-testid={`coach-pin-${c.conversation_id}`} className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-[#D4AF37]" aria-label="Pin">{c.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</button>
                        <button onClick={(e) => rename(c, e)} data-testid={`coach-rename-${c.conversation_id}`} className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-[#D4AF37]" aria-label="Rename"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={(e) => del(c.conversation_id, e)} data-testid={`coach-delete-${c.conversation_id}`} className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-red-400" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-coach-messages">
                    {messages.length === 0 && !loading && (
                      <div className="pt-2" data-testid="ai-coach-empty">
                        <div className="flex items-start gap-2.5 mb-4">
                          <div className="w-7 h-7 rounded-lg fv-gold-gradient flex items-center justify-center shrink-0"><Bot className="w-3.5 h-3.5 text-[#0A0A0B]" /></div>
                          <div className="bg-[#1C1C1F] text-[#E4E4E7] rounded-2xl px-3.5 py-2.5 text-sm">{cfg.greeting}</div>
                        </div>
                        <div className="space-y-2">
                          {cfg.actions.map(([label, prompt]) => (
                            <button key={label} onClick={() => send(prompt)} disabled={atLimit} data-testid="ai-coach-quick-action"
                              className="w-full text-left text-sm text-[#D4D4D8] border border-white/10 rounded-xl px-3.5 py-2.5 hover:border-[#D4AF37]/40 hover:bg-white/5 transition-colors disabled:opacity-40">
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {messages.map((m) => <Bubble key={m._key} role={m.role} content={m.content} />)}
                    {loading && <div className="flex gap-2"><div className="w-7 h-7 rounded-lg fv-gold-gradient flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-[#0A0A0B]" /></div><div className="bg-[#1C1C1F] rounded-2xl px-4 py-3 flex gap-1.5">{[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}</div></div>}
                  </div>

                  <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/8 flex gap-2">
                    <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-coach-input"
                      placeholder={atLimit ? "Monthly AI limit reached" : "Ask your coach anything…"} disabled={atLimit}
                      aria-label="Message the AI coach"
                      className="flex-1 bg-[#121214] border border-white/10 rounded-xl px-4 h-11 text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50 disabled:opacity-50" />
                    <button type="submit" disabled={loading || !input.trim() || atLimit} data-testid="ai-coach-send"
                      className="fv-gold-gradient text-[#0A0A0B] h-11 w-11 rounded-xl flex items-center justify-center disabled:opacity-40 shrink-0" aria-label="Send"><Send className="w-4.5 h-4.5" /></button>
                  </form>
                  <p className="px-4 pb-2 text-[10px] text-[#52525B]">Educational guidance only — not legal, tax, credit, or funding advice.</p>
                </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
